/**
 * Layout primitives.
 *
 * These own *structure* — how wide a column is, how a grid wraps, how much air
 * sits between sections — while carrying no colour and no business meaning.
 * Every measurement resolves to a token, so a different theme can make the same
 * page dense or airy without any page component changing.
 *
 * 布局基础组件。
 *
 * 这些组件只管「结构」——栏宽多少、栅格如何换行、区块之间留多少气——不含颜色、
 * 不含业务含义。所有度量都解析到令牌，因此换一个主题就能把同一个页面变紧凑或变
 * 宽松，页面组件一行都不用动。
 */

import { cx } from './primitives.jsx'

/**
 * Horizontally centred content column.
 *
 * 水平居中的内容栏。
 *
 * @param {object} props Props / 属性。
 * @param {'max'|'wide'|'narrow'|'prose'} [props.width] Which max-width token / 使用哪个最大宽度令牌。
 * @param {string} [props.as] Element tag / 元素标签。
 * @returns {JSX.Element} Container / 容器。
 */
export function Container({ width = 'max', as: Tag = 'div', className, children, ...rest }) {
  return (
    <Tag className={cx('ui-container', `ui-container--${width}`, className)} {...rest}>
      {children}
    </Tag>
  )
}

/**
 * Vertical band with section rhythm.
 *
 * 带区块纵向节奏的横幅区。
 *
 * @param {object} props Props / 属性。
 * @param {'default'|'subtle'} [props.tone] Background step / 背景层级。
 * @param {boolean} [props.divided] Draw a top hairline / 是否画顶部发丝线。
 * @returns {JSX.Element} Section / 区块。
 */
export function Section({ tone = 'default', divided = false, as: Tag = 'section', className, children, ...rest }) {
  return (
    <Tag
      className={cx('ui-section', `ui-section--${tone}`, divided && 'ui-section--divided', className)}
      {...rest}
    >
      {children}
    </Tag>
  )
}

/**
 * Page header: eyebrow, title, one supporting line, optional actions.
 *
 * Kept as a component rather than repeated markup so every page in the site
 * opens with identical structure — that repetition is most of what makes a set
 * of pages feel like one product.
 *
 * 页头：眉标、标题、一行说明、可选操作区。做成组件而不是重复标记，
 * 是为了让站内每个页面都以完全相同的结构开场——这种重复正是「一组页面像一个产品」
 * 的主要来源。
 *
 * @param {object} props Props / 属性。
 * @param {React.ReactNode} [props.eyebrow] Overline text / 眉标文字。
 * @param {React.ReactNode} props.title Page title / 页面标题。
 * @param {React.ReactNode} [props.lede] One supporting sentence / 一句说明。
 * @param {React.ReactNode} [props.actions] Right-aligned controls / 右侧操作。
 * @returns {JSX.Element} Page head / 页头。
 */
export function PageHead({ eyebrow, title, lede, actions, className, children }) {
  return (
    <header className={cx('ui-pagehead', className)}>
      <div className="ui-pagehead__main">
        {eyebrow && <p className="ui-overline">{eyebrow}</p>}
        <h1 className="ui-pagehead__title">{title}</h1>
        {lede && <p className="ui-pagehead__lede">{lede}</p>}
      </div>
      {actions && <div className="ui-pagehead__actions">{actions}</div>}
      {children}
    </header>
  )
}

/**
 * Section heading with an optional right-aligned count or link.
 *
 * 区块标题，右侧可放计数或链接。
 *
 * @param {object} props Props / 属性。
 * @param {React.ReactNode} props.title Heading text / 标题文字。
 * @param {React.ReactNode} [props.aside] Right-aligned content / 右侧内容。
 * @param {2|3} [props.level] Heading level / 标题层级。
 * @returns {JSX.Element} Section heading / 区块标题。
 */
export function SectionHead({ title, aside, level = 2, className }) {
  const Tag = level === 3 ? 'h3' : 'h2'
  return (
    <div className={cx('ui-sectionhead', className)}>
      <Tag className="ui-sectionhead__title">{title}</Tag>
      {aside && <div className="ui-sectionhead__aside">{aside}</div>}
    </div>
  )
}

/**
 * Vertical stack with a uniform gap.
 *
 * 等间距纵向堆叠。
 *
 * @param {object} props Props / 属性。
 * @param {'tight'|'base'|'loose'} [props.gap] Gap step / 间距档位。
 * @returns {JSX.Element} Stack / 堆叠容器。
 */
export function Stack({ gap = 'base', as: Tag = 'div', className, children, ...rest }) {
  return (
    <Tag className={cx('ui-stack', `ui-stack--${gap}`, className)} {...rest}>
      {children}
    </Tag>
  )
}

/**
 * Horizontal row that wraps.
 *
 * 可换行的横向排列。
 *
 * @param {object} props Props / 属性。
 * @param {'tight'|'base'|'loose'} [props.gap] Gap step / 间距档位。
 * @param {'start'|'center'|'between'|'end'} [props.justify] Main-axis alignment / 主轴对齐。
 * @param {'start'|'center'|'baseline'} [props.align] Cross-axis alignment / 交叉轴对齐。
 * @returns {JSX.Element} Row / 横向容器。
 */
export function Row({ gap = 'base', justify = 'start', align = 'center', as: Tag = 'div', className, children, ...rest }) {
  return (
    <Tag
      className={cx('ui-row', `ui-row--gap-${gap}`, `ui-row--justify-${justify}`, `ui-row--align-${align}`, className)}
      {...rest}
    >
      {children}
    </Tag>
  )
}

/**
 * Auto-filling card grid.
 *
 * The minimum column width arrives as a CSS variable rather than a literal
 * declaration, so the theme still decides the gap and the breakpoint behaviour.
 *
 * 自动填充的卡片栅格。最小列宽以 CSS 变量传入而非字面声明，间距和断点行为仍由
 * 主题决定。
 *
 * @param {object} props Props / 属性。
 * @param {string} [props.min] Minimum column width, e.g. '300px' / 最小列宽。
 * @param {number} [props.columns] Fixed column count instead of auto-fill / 固定列数（替代自动填充）。
 * @returns {JSX.Element} Grid / 栅格。
 */
export function Grid({ min, columns, className, children, style, ...rest }) {
  const vars = {}
  if (min) vars['--grid-min'] = min
  if (columns) vars['--grid-cols'] = String(columns)
  return (
    <div
      className={cx('ui-grid', columns ? 'ui-grid--fixed' : 'ui-grid--auto', className)}
      style={{ ...vars, ...style }}
      {...rest}
    >
      {children}
    </div>
  )
}

/**
 * Card — a bordered content block.
 *
 * Note there is no `elevated` variant: this design language separates content
 * with hairlines and whitespace, never with shadow. Shadow belongs to things
 * that genuinely float (menus, modals), which is what `Modal` uses.
 *
 * 卡片 —— 带边框的内容块。注意没有 elevated 变体：本设计语言用发丝线和留白分隔
 * 内容，从不用投影。投影属于真正浮起的元素（菜单、弹窗），那是 Modal 的事。
 *
 * @param {object} props Props / 属性。
 * @param {boolean} [props.interactive] Apply hover affordance / 是否带 hover 反馈。
 * @param {'default'|'flush'} [props.padding] Padding step / 内边距档位。
 * @returns {JSX.Element} Card / 卡片。
 */
export function Card({ interactive = false, padding = 'default', as: Tag = 'div', className, children, ...rest }) {
  return (
    <Tag
      className={cx('ui-card', `ui-card--pad-${padding}`, interactive && 'ui-card--interactive', className)}
      {...rest}
    >
      {children}
    </Tag>
  )
}

/**
 * Prose column — long-form text clamped to a comfortable measure.
 *
 * 正文栏 —— 长文本限制在舒适的行长内。
 *
 * @param {object} props Props / 属性。
 * @returns {JSX.Element} Prose container / 正文容器。
 */
export function Prose({ as: Tag = 'div', className, children, ...rest }) {
  return (
    <Tag className={cx('ui-prose', className)} {...rest}>
      {children}
    </Tag>
  )
}

/**
 * Fixed-aspect media frame.
 *
 * Reserving the box before the image loads is what stops a page from jumping as
 * photos arrive.
 *
 * 固定宽高比的媒体框。在图片加载前就把盒子占好，是页面不因图片陆续到达而跳动的
 * 关键。
 *
 * @param {object} props Props / 属性。
 * @param {string} [props.ratio] CSS aspect-ratio, e.g. '16 / 9' / 宽高比。
 * @returns {JSX.Element} Media frame / 媒体框。
 */
export function Media({ ratio, className, children, style, ...rest }) {
  return (
    <div
      className={cx('ui-media', className)}
      style={ratio ? { '--media-ratio': ratio, ...style } : style}
      {...rest}
    >
      {children}
    </div>
  )
}
